import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "../context/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    const res = await resetPassword(token.trim(), password);
    setLoading(false);
    if (res.ok) {
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 1400);
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthLayout testid="reset-page" quote="Set a new password and get back to selling tickets.">
      <div className="gt-label mb-3">Password reset</div>
      <h2 className="font-heading text-4xl font-black tracking-tighter mb-2">Set a new password</h2>
      <p className="text-[var(--gt-ink-2)] text-sm mb-8">
        Enter your reset token and choose a new password.
      </p>

      {success ? (
        <div className="gt-card flex items-start gap-3" data-testid="reset-success">
          <CheckCircle2 className="text-[var(--gt-accent)] shrink-0 mt-0.5" size={20} />
          <div>
            <div className="font-heading font-bold text-lg">Password updated</div>
            <div className="text-sm text-[var(--gt-ink-2)]">Redirecting you to log in…</div>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="reset-form">
          <div>
            <label className="gt-form-label" htmlFor="token">Reset token</label>
            <input
              id="token"
              className="gt-input font-mono text-xs"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your reset token"
              required
              data-testid="reset-token-input"
            />
          </div>
          <div>
            <label className="gt-form-label" htmlFor="password">New password</label>
            <input
              id="password"
              type="password"
              className="gt-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              minLength={8}
              required
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <label className="gt-form-label" htmlFor="confirm">Confirm new password</label>
            <input
              id="confirm"
              type="password"
              className="gt-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
              data-testid="reset-confirm-input"
            />
          </div>

          {error && (
            <div className="text-sm text-[var(--gt-accent)] font-medium" data-testid="reset-error">
              {error}
            </div>
          )}

          <button type="submit" className="gt-btn-primary" disabled={loading} data-testid="reset-submit-btn">
            {loading && <Loader2 size={16} className="animate-spin" />}
            Update password
          </button>

          <div className="text-xs text-[var(--gt-ink-3)]">
            <Link to="/login" className="text-[var(--gt-ink)] font-semibold underline underline-offset-4">Back to login</Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
